import { TooltipDirective } from '../../../../../auxiliares/tooltip/tooltip.directive';
import { BOTONES } from '../../../../../componentes/botones';
import { DatePipe } from '@angular/common';
import { Component, ViewEncapsulation, inject } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatCardModule } from '@angular/material/card';
import { CardHeaderComponent } from '../../../../../componentes/card-header/card-header.component';
import {
  ICliente,
  ICreateConfigReenvio,
  IConfigReenvio,
  IFilter,
  ILogReenvio,
} from 'modelos/src';
import { HelperService } from '../../../../../auxiliares/servicios/helper.service';
import { ConfigReenviosService } from '../../../../../auxiliares/servicios/http/config-reenvios.service';
import { ClientesService } from '../../../../../auxiliares/servicios/http/clientes.service';
import { LogReenviosService } from '../../../../../auxiliares/servicios/http/log-reenvios.service';
import { TrackersService } from '../../../../../auxiliares/servicios/http/trackers.service';
import { DialogService } from '../../../../../auxiliares/dialog/dialog.service';
import { LoginService } from '../../../../login/login.service';

export interface IEmergenciaAvlData {
  /** Tracker sobre el que se declara la emergencia (agrupación "Entidad") */
  idTracker: string;
  /** Patente o identificación, para el encabezado */
  nombreEntidad?: string;
  /** Patente del vehículo: es el deviceid con el que se identifica el
   *  objetivo ante Suite911. Si no viene, el popup la resuelve pidiendo el
   *  tracker — así lo puede abrir una pantalla que no la tenga a mano. */
  patente?: string;
  /** uniqueId del tracker: deviceid de respaldo cuando el vehículo no tiene
   *  patente. */
  uniqueId?: string;
  /** Config existente: abre en modo ajuste (extender / detener) */
  config?: IConfigReenvio;
}

interface IOpcionDuracion {
  label: string;
  minutos: number;
}

/**
 * Popup de emergencia AVL: arranca, extiende o detiene el reenvío de
 * posiciones a Policía de la Ciudad (esquema Objetivos AVL de la DGSPCB) para
 * UN vehículo.
 *
 * Es un componente de uso compartido a propósito: se abre desde el listado de
 * vehículos, desde la ficha del vehículo y desde el dialog de reenvíos, y
 * cualquier pantalla nueva que necesite declarar una emergencia lo reusa
 * pasándole `idTracker`. Todo lo específico del contexto entra por
 * `IEmergenciaAvlData`; el componente no sabe de dónde lo abrieron.
 *
 * Crea siempre `agrupacionReenvio: 'Entidad'` con método `'Objetivo AVL'` y
 * `periodoInicio`/`periodoFin`: al vencer el período el reenvío se corta solo,
 * sin depender de que alguien se acuerde de apagarlo. Las credenciales (URL y
 * token) NO se cargan acá: son del prestador y viven en el cliente.
 */
@Component({
  selector: 'app-emergencia-avl-dialog',
  templateUrl: './emergencia-avl-dialog.component.html',
  styleUrl: './emergencia-avl-dialog.component.scss',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  imports: [TooltipDirective, 
    DatePipe,
    ReactiveFormsModule,
    MatDialogModule,
    BOTONES,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    MatAutocompleteModule,
    MatProgressSpinnerModule,
    MatCardModule,
    CardHeaderComponent,
  ],
})
export class EmergenciaAvlDialogComponent {
  public data = inject<IEmergenciaAvlData>(MAT_DIALOG_DATA);
  public dialogRef =
    inject<MatDialogRef<EmergenciaAvlDialogComponent>>(MatDialogRef);
  private service = inject(ConfigReenviosService);
  private trackersService = inject(TrackersService);
  private clientesService = inject(ClientesService);
  private logReenviosService = inject(LogReenviosService);
  private helper = inject(HelperService);
  private dialogService = inject(DialogService);

  /** Arranca con la copia del login y se reemplaza por la de la API en el
   *  constructor: la de sessionStorage queda vieja hasta el próximo login, y
   *  activar el módulo o cargar el token no se reflejaba en el aviso. */
  public clientePropio?: ICliente = LoginService.getCliente();
  /** Con una config existente el popup ajusta en lugar de crear. Si no la
   *  pasaron pero el tracker ya tiene una emergencia vigente, se busca y se
   *  abre en modo ajuste: declarar otra duplicaba el envío a Policía. */
  public config = this.data?.config;
  public guardando = false;
  /** Mientras se busca una emergencia vigente no se puede declarar. */
  public buscandoVigente = !this.data?.config;

  public puedeVerEnvios = this.helper.tienePermiso('Logs - Ver logs reenvios');
  public envios?: ILogReenvio[];
  public cargandoEnvios = false;

  public duraciones: IOpcionDuracion[] = this.armarDuraciones();

  /** El deviceid es la patente; sin patente, el uniqueId del tracker. La
   *  patente gana también sobre el deviceid guardado en una config vieja (que
   *  llevaba el uniqueId): al ajustarla queda corregida. Misma regla que
   *  gestion-trackers-go y api-gestion. */
  public patente = this.data?.patente?.trim() || undefined;

  public form = new FormGroup({
    /** Minutos, en crudo. Se tipea directo o se elige de la lista sugerida. */
    duracion: new FormControl<number | null>(this.duracionPorDefecto()),
    deviceid: new FormControl<string>(
      this.patente ||
        this.config?.opcionesReenvio?.opcionesObjetivoAVL?.deviceid ||
        this.data?.uniqueId ||
        '',
    ),
    nroCarta: new FormControl<string>(
      this.config?.opcionesReenvio?.opcionesObjetivoAVL?.nroCarta ?? '',
    ),
  });

  constructor() {
    if (!this.patente && this.data?.idTracker) {
      this.resolverDeviceId();
    }
    this.resolverCliente();
    if (this.config) {
      this.listarEnvios();
    } else {
      this.buscarVigente();
    }
  }

  private async resolverCliente(): Promise<void> {
    try {
      const cliente = await this.clientesService.getPropio();
      if (!cliente) return;
      this.clientePropio = cliente;
      // La duración por defecto sale del cliente: si todavía no la tocaron, se
      // recalcula con el dato fresco.
      if (!this.esAjuste && this.form.controls.duracion.pristine) {
        this.form.patchValue({ duracion: this.duracionPorDefecto() });
      }
      this.duraciones = this.armarDuraciones();
    } catch (error) {
      console.error('Error al resolver el cliente', error);
    }
  }

  /** Emergencia ya declarada sobre este tracker. El listado y la ficha del
   *  vehículo abren el popup sin config, y declarar de nuevo creaba una
   *  segunda config: trackers-go mandaba cada posición dos veces. */
  private async buscarVigente(): Promise<void> {
    try {
      const filter: IFilter<IConfigReenvio> = {
        agrupacionReenvio: 'Entidad',
        idEntidadReenvio: this.data.idTracker,
        activo: true,
        'opcionesReenvio.metodo': 'Objetivo AVL',
      } as IFilter<IConfigReenvio>;
      const listado = await this.service.getFiltered({
        filter: JSON.stringify(filter),
        sort: '-fechaCreacion',
        limit: 10,
        excludeTotalCount: true,
        // La config puede estar a nombre de un cliente hijo (la declaró un
        // usuario de ese cliente): también cuenta como emergencia vigente.
        includeChildren: true,
        childrenLevel: 0,
      });
      const ahora = Date.now();
      const vigente = listado?.datos?.find(
        (c) =>
          (!c.periodoInicio || new Date(c.periodoInicio).getTime() <= ahora) &&
          (!c.periodoFin || new Date(c.periodoFin).getTime() >= ahora),
      );
      if (vigente) {
        this.config = vigente;
        const avl = vigente.opcionesReenvio?.opcionesObjetivoAVL;
        this.form.patchValue({
          deviceid: this.patente || avl?.deviceid || this.form.value.deviceid,
          nroCarta: avl?.nroCarta ?? '',
        });
        this.listarEnvios();
      }
    } catch (error) {
      console.error('Error al buscar una emergencia vigente', error);
    } finally {
      this.buscandoVigente = false;
    }
  }

  /** Últimos envíos a Suite911 de esta emergencia. Es lo que confirma que la
   *  Policía está recibiendo: un token rechazado se ve acá en el momento, en
   *  vez de descubrirlo cuando llaman preguntando. */
  public async listarEnvios(): Promise<void> {
    if (!this.puedeVerEnvios || !this.config?.periodoInicio) return;
    this.cargandoEnvios = true;
    try {
      const filter: IFilter<ILogReenvio> = {
        idEntidad: this.data.idTracker,
        protocolo: 'Objetivo AVL',
        fecha: { $gte: this.config.periodoInicio },
      } as IFilter<ILogReenvio>;
      const listado = await this.logReenviosService.getFiltered({
        filter: JSON.stringify(filter),
        select: 'fecha ack error',
        sort: '-fecha',
        limit: 10,
        excludeTotalCount: true,
        // Los envíos están a nombre del cliente del tracker (a menudo un hijo):
        // sin esto la lista salía vacía.
        includeChildren: true,
        childrenLevel: 0,
      });
      this.envios = listado?.datos ?? [];
    } catch (error) {
      console.error('Error al listar los envíos de la emergencia', error);
    } finally {
      this.cargandoEnvios = false;
    }
  }

  /** Cuando quien abre el popup no pasó la patente se pide el tracker (trae
   *  el activo), en vez de mostrar el aviso de "sin patente" que en ese caso
   *  sería falso. */
  private async resolverDeviceId(): Promise<void> {
    try {
      const tracker = await this.trackersService.getById(this.data.idTracker);
      const patente = tracker?.activo?.vehiculo?.patente?.trim();
      if (patente) {
        this.patente = patente;
        this.form.patchValue({ deviceid: patente });
      } else if (!this.form.value.deviceid && tracker?.uniqueId) {
        this.form.patchValue({ deviceid: tracker.uniqueId });
      }
    } catch (error) {
      console.error('Error al resolver la patente del vehículo', error);
    }
  }

  /** Sin patente se identifica con el uniqueId: se avisa, porque es lo que
   *  hay que cargar en e911 en ese caso. */
  public get usaIdUnico(): boolean {
    return (
      !!this.form.value.deviceid && this.form.value.deviceid !== this.patente
    );
  }

  public get esAjuste(): boolean {
    return !!this.config?._id;
  }

  /** La config abierta está transmitiendo ahora mismo. Desde el dialog de
   *  reenvíos se puede abrir una ya vencida o detenida: ahí no corresponde el
   *  aviso de "activa". */
  /** "Desde el 23/09 09:04 hasta el 23/09 13:04", para el aviso de activa. */
  public get periodoActivo(): string {
    const inicio = this.config?.periodoInicio;
    const fin = this.config?.periodoFin;
    const desde = inicio
      ? `Desde el ${this.fechaLegible(new Date(inicio))}`
      : 'Activa';
    return fin
      ? `${desde} hasta el ${this.fechaLegible(new Date(fin))}`
      : desde;
  }

  public get activaAhora(): boolean {
    const c = this.config;
    if (!c?._id || c.activo === false) return false;
    const ahora = Date.now();
    const empezo =
      !c.periodoInicio || new Date(c.periodoInicio).getTime() <= ahora;
    const sigue = !c.periodoFin || new Date(c.periodoFin).getTime() >= ahora;
    return empezo && sigue;
  }

  /** Minutos efectivos. El input es numérico, pero un `<input type="number">`
   *  vacío entrega `null` y uno recién tipeado puede llegar como string. */
  public get minutos(): number | null {
    const min = Number(this.form.value.duracion);
    return Number.isFinite(min) && min > 0 ? Math.round(min) : null;
  }

  /** Traducción legible de lo tipeado: "2 horas", "1 hora 30 min". */
  public get duracionEnPalabras(): string | null {
    const min = this.minutos;
    return min ? this.etiquetaDuracion(min) : null;
  }

  /** Fin calculado, para mostrarlo antes de confirmar. */
  public get fin(): Date | null {
    const min = this.minutos;
    return min ? new Date(Date.now() + min * 60000) : null;
  }

  /** El módulo tiene que estar activo y con credenciales en el cliente que
   *  crea el reenvío: el token identifica al prestador ante Suite911. Si falta
   *  algo se avisa acá y no cuando el envío falla en silencio en el backend. */
  public get faltaConfig(): boolean {
    const avl = this.clientePropio?.config?.integracionObjetivosAVL;
    return !avl?.activo || !avl?.url || !avl?.token;
  }

  public get puedeGuardar(): boolean {
    if (
      this.guardando ||
      this.buscandoVigente ||
      !this.minutos ||
      this.faltaConfig
    ) {
      return false;
    }
    return !!this.form.value.deviceid;
  }

  private duracionPorDefecto(): number {
    // El cliente fija cuánto dura por defecto una emergencia
    // (config.integracionObjetivosAVL.minutosDuracion). Sin eso, 2 horas.
    const min =
      this.clientePropio?.config?.integracionObjetivosAVL?.minutosDuracion;
    return min && Number(min) > 0 ? Number(min) : 120;
  }

  /** Sugerencias del desplegable: presets + el default del cliente si no
   *  coincide con ninguno. Son atajos, no las únicas opciones: el campo acepta
   *  cualquier número de minutos tipeado a mano. */
  private armarDuraciones(): IOpcionDuracion[] {
    const presets = [5, 10, 15, 30, 45, 60, 120, 240, 480, 1440];
    const propio = this.duracionPorDefecto();
    const minutos = presets.includes(propio)
      ? presets
      : [...presets, propio].sort((a, b) => a - b);

    return minutos.map((m) => ({
      label: this.etiquetaDuracion(m),
      minutos: m,
    }));
  }

  /** Fecha en texto para los diálogos de confirmación (no hay pipes acá). */
  private fechaLegible(fecha: Date | null): string {
    if (!fecha) return 'sin fecha';
    const dd = `${fecha.getDate()}`.padStart(2, '0');
    const mm = `${fecha.getMonth() + 1}`.padStart(2, '0');
    const hh = `${fecha.getHours()}`.padStart(2, '0');
    const mi = `${fecha.getMinutes()}`.padStart(2, '0');
    return `${dd}/${mm}/${fecha.getFullYear()} ${hh}:${mi}`;
  }

  private etiquetaDuracion(minutos: number): string {
    if (minutos < 60) return `${minutos} minutos`;
    const horas = Math.floor(minutos / 60);
    const resto = minutos % 60;
    const txtHoras = horas === 1 ? '1 hora' : `${horas} horas`;
    return resto ? `${txtHoras} ${resto} min` : txtHoras;
  }

  /** Detiene el reenvío ya mismo: `activo: false` y el período cerrado en este
   *  instante. Se tocan los dos porque el camino Básico de trackers-go filtra
   *  por vigencia pero NO por `activo`; con el período cerrado corta seguro
   *  cualquiera sea el método. */
  public async detener(): Promise<void> {
    const id = this.config?._id;
    if (!id) return;
    const ok = await this.dialogService.confirm(
      'Detener emergencia',
      'Se deja de transmitir la posición a Policía de la Ciudad en este momento. ¿Confirmás?',
      'Detener',
    );
    if (!ok) return;

    this.guardando = true;
    try {
      const actualizada = await this.service.update(id, {
        activo: false,
        periodoFin: new Date().toISOString(),
      });
      this.helper.notifSuccess('Emergencia detenida');
      this.dialogRef.close(actualizada);
    } catch (error) {
      console.error('Error al detener la emergencia AVL', error);
      this.helper.notifWarn('No se pudo detener la emergencia');
      this.guardando = false;
    }
  }

  public async guardar(): Promise<void> {
    const minutos = this.minutos;
    if (!minutos || !this.data?.idTracker) return;

    // Se confirma también al declarar: arranca una transmisión a Policía de la
    // Ciudad sobre un vehículo concreto, y el texto repite el device y hasta
    // cuándo va — que es lo que conviene releer antes de apretar.
    const hasta = this.fin;
    const ok = await this.dialogService.confirm(
      this.esAjuste ? 'Ajustar emergencia' : 'Declarar emergencia',
      this.esAjuste
        ? `Se extiende la transmisión de ${this.form.value.deviceid} hasta el ${this.fechaLegible(hasta)}. ¿Confirmás?`
        : `Se empieza a transmitir la posición de ${this.form.value.deviceid} a Policía de la Ciudad hasta el ${this.fechaLegible(hasta)}. ¿Confirmás?`,
      this.esAjuste ? 'Guardar' : 'Declarar',
    );
    if (!ok) return;

    this.guardando = true;

    const ahora = new Date();
    const periodoFin = new Date(
      ahora.getTime() + minutos * 60000,
    ).toISOString();
    const opcionesReenvio: ICreateConfigReenvio['opcionesReenvio'] = {
      metodo: 'Objetivo AVL',
      opcionesObjetivoAVL: {
        deviceid: this.form.value.deviceid ?? undefined,
        nroCarta: this.form.value.nroCarta || undefined,
      },
    };

    // Ajuste: el nuevo fin se cuenta desde ahora, no desde el inicio original.
    // Reactiva la config si estaba detenida, que es lo que espera quien la
    // vuelve a extender.
    if (this.esAjuste && this.config?._id) {
      try {
        const actualizada = await this.service.update(this.config._id, {
          activo: true,
          periodoFin,
          opcionesReenvio,
        });
        this.helper.notifSuccess('Emergencia actualizada');
        this.dialogRef.close(actualizada);
      } catch (error) {
        console.error('Error al ajustar la emergencia AVL', error);
        this.helper.notifWarn('No se pudo ajustar la emergencia');
        this.guardando = false;
      }
      return;
    }

    const config: ICreateConfigReenvio = {
      activo: true,
      agrupacionReenvio: 'Entidad',
      idEntidadReenvio: this.data.idTracker,
      periodoInicio: ahora.toISOString(),
      periodoFin,
      opcionesReenvio,
      // idCliente va vacío a propósito: el backend lo completa con el cliente
      // del permiso, que es el que tiene las credenciales. Si un padre declara
      // la emergencia de un vehículo de un hijo, la config queda a nombre del
      // padre y se usa SU token.
    };

    try {
      const creada = await this.service.create(config);
      this.helper.notifSuccess('Emergencia declarada, se está transmitiendo');
      this.dialogRef.close(creada);
    } catch (error) {
      console.error('Error al declarar la emergencia AVL', error);
      this.helper.notifWarn('No se pudo declarar la emergencia');
      this.guardando = false;
    }
  }
}
